import api from './axios';

/**
 * Admin API - User Management
 */

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'parent' | 'staff' | 'admin';
  created_at: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

export interface UsersListParams {
  role?: 'parent' | 'staff' | 'admin';
  search?: string;
  limit?: number;
  offset?: number;
}

export const getUsers = async (params?: UsersListParams): Promise<UsersListResponse> => {
  const { data } = await api.get<UsersListResponse>('/api/admin/users', { params });
  return data;
};

export interface ChangeRolePayload {
  role: 'parent' | 'staff' | 'admin';
}

export const changeUserRole = async (userId: number, payload: ChangeRolePayload): Promise<User> => {
  const { data } = await api.put<{ user: User }>(`/api/admin/users/${userId}/role`, payload);
  return data.user;
};

/**
 * Admin API - Family Management
 */

export interface FamilyListItem {
  id: number;
  family_name: string;
  status: string;
  primary_contact: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  student_count: number;
  created_at: string;
}

export interface FamiliesListResponse {
  families: FamilyListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface FamiliesListParams {
  search?: string;
  limit?: number;
  offset?: number;
}

export const getFamilies = async (params?: FamiliesListParams): Promise<FamiliesListResponse> => {
  const { data } = await api.get<FamiliesListResponse>('/api/admin/families', { params });
  return data;
};

export interface FamilyDetails {
  id: number;
  status: string;
  primaryContact: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  students: Array<{
    id: number;
    first_name: string;
    last_name: string;
    grade: string;
    pickup_type: string;
    medical_notes: string | null;
  }>;
  authorizedPersons: Array<{
    id: number;
    first_name: string;
    last_name: string;
    relationship: string;
    phone: string;
    email: string;
    has_pickup_rights: boolean;
    id_check_required: boolean;
  }>;
  created_at: string;
}

export const getFamilyDetails = async (familyId: number): Promise<FamilyDetails> => {
  const { data } = await api.get<FamilyDetails>(`/api/admin/families/${familyId}`);
  return data;
};

/**
 * Admin API - Statistics
 */

export interface AdminStats {
  users: {
    total: number;
    parents: number;
    staff: number;
    admins: number;
  };
  families: number;
  registered_students: number;
  today: {
    check_ins: number;
    dismissals: number;
    currently_checked_in: number;
  };
}

export const getStats = async (): Promise<AdminStats> => {
  const { data } = await api.get<AdminStats>('/api/admin/stats');
  return data;
};
